import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { getAuthCredentials } from "../../utils/auth";
import dayjs from "dayjs";

const SalesTransactions = ({ dateRange }) => {
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const rowsPerPage = 20;
  const { token, posId } = getAuthCredentials();

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/sales/transactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            posId,
            startDate: dayjs(dateRange[0]).format("YYYY-MM-DDTHH:mm:ss"),
            endDate: dayjs(dateRange[1]).format("YYYY-MM-DDTHH:mm:ss"),
            page: page + 1,
            limit: rowsPerPage,
            search,
          },
        }
      );

      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [dateRange, page, search]);

  const handleChangePage = (_, newPage) => setPage(newPage);

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto", mt: 5 }}>
      <Typography variant="h5" align="center" gutterBottom>
        Transaction Logs
      </Typography>

      <TextField
        label="Search Item Name"
        variant="outlined"
        size="small"
        fullWidth
        sx={{ mb: 2 }}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
      />

      {loading ? (
        <Box
          sx={{
            mt: 10,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "40vh",
          }}
        >
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">Fetching Transactions...</Typography>
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date/Time</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Subcategory</TableCell>
                  <TableCell>Sales Type</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <TableCell>
                      {dayjs(row.date).format("YYYY-MM-DD hh:mm:ss A")}
                    </TableCell>
                    <TableCell>{row.description || "N/A"}</TableCell>
                    <TableCell>{row.subcategory}</TableCell>
                    <TableCell>{row.salesType || "-"}</TableCell>
                    <TableCell>{row.discountType || "None"}</TableCell>
                    <TableCell align="right">
                      ₱{(row.price || 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No transactions found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default SalesTransactions;
